import React, { useCallback, useEffect, useId, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { createEnquiryForm, getGetinTouch } from "../../api/commonApi"; // your API file
import { showNotification } from "../../Store/Slice/headerSlice";
import { useDispatch } from "react-redux";

const ContactSection = ({ id = "contact" }) => {
  const dispatch = useDispatch();

  const initialForm = useMemo(
    () => ({ name: "", emailId: "", mobileNumber: "", purpose: "" }),
    []
  );

  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [imageUrl, setImageUrl] = useState("");

  const nameId = useId();
  const emailId = useId();
  const phoneId = useId();
  const purposeId = useId();

  useEffect(() => {
    (async () => {
      try {
        const res = await getGetinTouch();
        const url = res?.data?.imageUrl;
        setImageUrl(url || "");
      } catch (err) {
        setImageUrl("");
      }
    })();
  }, []);

  const validate = useCallback((data) => {
    const e = {};
    if (!data.name?.trim()) e.name = "Please enter your name.";

    const email = data.emailId?.trim();
    if (!email) {
      e.emailId = "Please enter your email address.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      e.emailId = "Enter a valid email address.";
    }

    const phone = data.mobileNumber || "";
    if (!phone) {
      e.mobileNumber = "Please enter your phone number.";
    } else if (!/^\d{10}$/.test(phone)) {
      e.mobileNumber = "Phone number must be exactly 10 digits.";
    }

    if (!data.purpose?.trim()) e.purpose = "Please enter a message.";
    return e;
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    if (name === "mobileNumber") {
      // allow only numbers and max 10 digits
      const numeric = value.replace(/\D/g, "").slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: numeric }));
      setErrors((prev) => ({ ...prev, [name]: undefined }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (submitting) return;

      const nextErrors = validate(formData);
      setErrors(nextErrors);
      if (Object.values(nextErrors).some(Boolean)) {
        dispatch(
          showNotification({
            message: "Please fix the highlighted fields.",
            type: "error",
          })
        );
        return;
      }

      setSubmitting(true);
      try {
        const res = await createEnquiryForm(formData);
        const ok =
          res?.statusCode === 200 || res?.status === 200 || res?.data?.success;

        if (ok) {
          dispatch(
            showNotification({
              message: "Message sent successfully!",
              type: "success",
            })
          );
          setFormData(initialForm);
        } else {
          const msg = res?.message || res?.data?.message || "Failed to send";
          dispatch(showNotification({ message: msg, type: "error" }));
        }
      } catch (err) {
        dispatch(
          showNotification({ message: "Something went wrong", type: "error" })
        );
      } finally {
        setSubmitting(false);
      }
    },
    [dispatch, formData, initialForm, submitting, validate]
  );

  return (
    <section id={id} className="container mx-auto px-4 pt-0 pb-16">
      <div className="relative flex items-center justify-center mb-12">
        <div className="w-full h-px bg-[linear-gradient(to_right,#FBFAF1,#87004C,#FBFAF1)]" />
        <span className="absolute px-6 py-2 rounded-full bg-[linear-gradient(to_right,#DFC355,#FDF880,#ECDA68)] text-[#87004C] font-medium shadow">
          Get The App
        </span>
      </div>

      <h2 className="text-center text-4xl md:text-6xl font-bold mb-12">
        <span className="heading-style text-black">Get In </span>
        <span className="heading-style text-[#87004C]">Touch</span>
      </h2>

      <div className="grid md:grid-cols-2 gap-12 items-center">
        <div className="flex justify-center">
          {imageUrl && (
            <img
              src={imageUrl}
              alt="Get in touch"
              loading="lazy"
              className="max-w-full h-auto rounded-xl shadow-sm"
            />
          )}
        </div>

        <form className="space-y-6 w-full" onSubmit={handleSubmit} noValidate>
          <div>
            <label htmlFor={nameId} className="block text-black mb-1">
              Name
            </label>
            <input
              id={nameId}
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter Name"
              className={`w-full border rounded-lg px-4 py-2 h-14 placeholder:text-[#757575] focus:outline-none ${
                errors.name ? "border-red-500" : "border-[#E3E3E3]"
              }`}
              required
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          <div>
            <label htmlFor={emailId} className="block text-black mb-1">
              Email Address
            </label>
            <input
              id={emailId}
              type="email"
              name="emailId"
              value={formData.emailId}
              onChange={handleChange}
              placeholder="Enter Email Address"
              className={`w-full border rounded-lg px-4 py-2 h-14 placeholder:text-[#757575] focus:outline-none ${
                errors.emailId ? "border-red-500" : "border-[#E3E3E3]"
              }`}
              required
            />
            {errors.emailId && (
              <p className="mt-1 text-sm text-red-600">{errors.emailId}</p>
            )}
          </div>

          <div>
            <label htmlFor={phoneId} className="block text-black mb-1">
              Phone Number
            </label>
            <input
              id={phoneId}
              type="tel"
              name="mobileNumber"
              value={formData.mobileNumber}
              onChange={handleChange}
              placeholder="Enter Phone Number"
              pattern="\\d{10}"
              maxLength={10}
              className={`w-full border rounded-lg px-4 py-2 h-14 placeholder:text-[#757575] focus:outline-none ${
                errors.mobileNumber ? "border-red-500" : "border-[#E3E3E3]"
              }`}
              required
            />
            {errors.mobileNumber && (
              <p className="mt-1 text-sm text-red-600">{errors.mobileNumber}</p>
            )}
          </div>

          <div>
            <label htmlFor={purposeId} className="block text-black mb-1">
              Message
            </label>
            <textarea
              id={purposeId}
              name="purpose"
              value={formData.purpose}
              onChange={handleChange}
              placeholder="Type Your Query Here....."
              rows={4}
              className={`w-full border rounded-lg px-4 py-2 placeholder:text-[#757575] focus:outline-none ${
                errors.purpose ? "border-red-500" : "border-[#E3E3E3]"
              }`}
              required
            />
            {errors.purpose && (
              <p className="mt-1 text-sm text-red-600">{errors.purpose}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#87004C] text-white font-medium py-3 rounded-lg transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? "Sending..." : "Send My Message"}
          </button>
        </form>
      </div>
    </section>
  );
};

ContactSection.propTypes = {
  id: PropTypes.string,
};

export default ContactSection;
